import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/auth';
import 'firebase/storage';
import 'firebase/functions';
import { firebaseConfig } from '../keys';

let firebaseApp;
if (!firebase.apps.length) {
    firebaseApp = firebase.initializeApp(firebaseConfig);
} else {
    firebaseApp = firebase.app();
}

const firestore = firebase.firestore();
const functions = firebase.functions();

// Configuration spéciale pour le développement et gestion CORS
if (process.env.NODE_ENV === 'development') {

    // Configuration Firestore pour développement
    try {
        firestore.settings({
            experimentalForceLongPolling: true,
            merge: true
        });
    } catch (error) {

    }

    // Configuration Auth pour développement
    const auth = firebase.auth();
    auth.languageCode = 'fr';

    // Gestion améliorée des erreurs réseau
    auth.onAuthStateChanged((user) => {
        if (user) {

        }
    });
}

const handleFirebaseError = (error) => {
    const errorMessages = {
        'permission-denied': 'Accès refusé. Vous n\'avez pas les droits nécessaires.',
        'unavailable': 'Service Firebase indisponible. Vérifiez votre connexion internet.',
        'not-found': 'Ressource introuvable.',
        'network-request-failed': 'Problème de connexion réseau. Vérifiez votre connexion internet.',
        'cancelled': 'Opération annulée.',
        'invalid-argument': 'Argument invalide fourni à une fonction Firebase.',
        'deadline-exceeded': 'Délai d\'opération dépassé.'
    };
    return errorMessages[error.code] || `Erreur: ${error.message}`;
};

const settings = {
    cacheSizeBytes: firebase.firestore.CACHE_SIZE_UNLIMITED
};

if (firebase.firestore.Settings && typeof firebase.firestore.Settings === 'function') {
    if ('ignoreUndefinedProperties' in firebase.firestore.Settings.prototype) {
        settings.ignoreUndefinedProperties = true;
    }
    if ('experimentalForceLongPolling' in firebase.firestore.Settings.prototype) {
        settings.experimentalForceLongPolling = true;
    }
    if ('experimentalAutoDetectLongPolling' in firebase.firestore.Settings.prototype) {
        settings.experimentalAutoDetectLongPolling = true;
    }
}

firestore.settings(settings);

firestore.enablePersistence({ synchronizeTabs: true }).catch(() => {});

const originalGet = firestore.collection;
firestore.collection = function() {
    const collection = originalGet.apply(this, arguments);
    const originalCollectionGet = collection.get;

    collection.get = function() {
        return originalCollectionGet.apply(this, arguments)
            .catch(error => {
                const errorMessage = handleFirebaseError(error);
                if (error.message) {
                    error.originalMessage = error.message;
                    error.message = errorMessage;
                }
                throw error;
            });
    };
    return collection;
};

export const normalizeUserData = (userData = {}) => {
    return {
        email: userData.email || '',
        name: userData.name || userData.displayName || '',
        profession: userData.profession || '',
        numeroOrdre: userData.numeroOrdre || '',
        telephone: userData.telephone || '',
        emailVerified: typeof userData.emailVerified === 'boolean' ? userData.emailVerified : false,
        status: userData.status || 'active',
        role: userData.role || 'user',
        subscription: userData.subscription || 'basic',
        subscriptionAmount: typeof userData.subscriptionAmount === 'number' ? userData.subscriptionAmount : 0,
        lastLogin: userData.lastLogin || null,
        createdAt: userData.createdAt || null,
        updatedAt: userData.updatedAt || null,
        ...userData
    };
};

export const getUserProfile = async (uid) => {
    if (!uid) throw new Error('ID utilisateur non fourni');

    try {
        const userDoc = await firestore.collection('users').doc(uid).get();

        if (userDoc.exists) {
            const userData = userDoc.data();
            return normalizeUserData({ id: userDoc.id, ...userData });
        }

        const authUser = firebase.auth().currentUser;
        if (authUser?.email) {
            const querySnapshot = await firestore
                .collection('users')
                .where('email', '==', authUser.email)
                .limit(1)
                .get();

            if (!querySnapshot.empty) {
                const userDoc = querySnapshot.docs[0];
                const userData = userDoc.data();
                return normalizeUserData({ id: userDoc.id, ...userData });
            }
        }

        return normalizeUserData({
            uid,
            email: authUser?.email || '',
            displayName: authUser?.displayName || '',
            photoURL: authUser?.photoURL || '',
            emailVerified: authUser?.emailVerified || false,
            createdAt: firebase.firestore.FieldValue.serverTimestamp()
        });
    } catch (error) {
        throw error;
    }
};

export const userCollection = firestore.collection("users");
export const productCollection = firestore.collection("products");
export const auth = firebase.auth();

export const waitForAuthState = () => {
    if (auth.currentUser) return Promise.resolve(auth.currentUser);

    return new Promise((resolve, reject) => {
        let unsubscribe = () => {};
        let authStateResolved = false;
        const stopListening = auth.onAuthStateChanged(
            user => {
                authStateResolved = true;
                unsubscribe();
                resolve(user);
            },
            error => {
                authStateResolved = true;
                unsubscribe();
                reject(error);
            }
        );

        unsubscribe = stopListening;
        if (authStateResolved) unsubscribe();
    });
};

export { firestore, firebaseApp, handleFirebaseError, functions, firebase };

export default firebase;